import { InputTextarea } from 'primereact/inputtextarea';
import React, { useEffect, useState } from 'react';

import { CoachingTabs } from '../../utils/typeConstants';
import VertexAI from '../vertexAI/VertexAI';
import VoiceToText from '../voiceToTextArea/voiceToText';
import './descriptionBox.scss';
import { DescriptionBoxProps } from './types';

const DescriptionBox = (props: DescriptionBoxProps) => {
    const [error, setError] = useState<string | null>(null);
    const [textAreaValue, setTextAreaValue] = useState<string>(props?.value || '');

    // Normalize line endings to Unix style (\n) to ensure consistent character counting
    const normalizeLineEndings = (text: string): string => {
        return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        let eventVal = e.target.value;

        // Normalize line endings to prevent discrepancies between UI and API
        eventVal = normalizeLineEndings(eventVal);

        // CRITICAL FIX: Enforce hard limit by truncating if necessary
        if (eventVal.length > props?.limit) {
            eventVal = eventVal.substring(0, props?.limit);
            setError(`Description cannot exceed ${props?.limit} characters`);
        }

        setTextAreaValue(eventVal);
        const trimmedVal = eventVal.trim();
        props?.onChange(trimmedVal);

        if (eventVal.length <= props?.limit && props?.via !== CoachingTabs.CoachingActionPlan) {
            if (trimmedVal.length > 0 && trimmedVal.length < 10) {
                setError('Description must be at least 10 characters');
            } else {
                setError(null);
            }
        }
    };

    useEffect(() => {
        setTextAreaValue(props?.value || '');
    }, [props?.value]);

    const updateFromAI = (value: string) => {
        // Normalize and ensure AI-generated content respects the limit
        const normalizedValue = normalizeLineEndings(value);
        const truncatedValue =
            normalizedValue.length > props?.limit
                ? normalizedValue.substring(0, props?.limit)
                : normalizedValue;
        setTextAreaValue(truncatedValue);
        props?.onChange(truncatedValue.trim());
    };

    const calculateMaxLength = () => {
        if (!props?.limit) return 0;
        const limit = props.limit; // safe to use directly after the guard above
        return props?.via === CoachingTabs.shoutOut ? limit - 50 : limit - 100;
    };

    return (
        <>
            <h6>{props?.title}</h6>

            <div className={`textarea-wrapper ${props?.className}`} style={{backgroundColor:'#FFFFFF'}} >
                <div className="textarea-body">
                    {props?.readOnly ? (
                        <div className="textarea-resizable-container">
                            <InputTextarea
                                id={props?.id}
                                autoResize={false}
                                className={` ${props?.className || 'borderRadius8 w-100'}`}
                                value={props?.responseDescription || ''}
                                readOnly
                            />
                        </div>
                    ) : (
                        <div className="textarea-resizable-container">
                            <InputTextarea
                                id={props?.id}
                                disabled={props?.disabled}
                                autoResize={false}
                                className={` ${props?.className || 'borderRadius8 w-100'}`}
                                onChange={handleChange}
                                maxLength={props?.limit}
                                value={textAreaValue}
                                placeholder={props?.placeholder || ''}
                            />

                            <div className="textarea-icons">
                                {textAreaValue.length > 0 && (
                                    <VertexAI
                                        aiInputText={textAreaValue}
                                        setTextAreaValue={updateFromAI}
                                        aiMaxLength={calculateMaxLength()}
                                        via={props?.via}
                                        tabIndex={props?.iconsTabIndex}
                                    />
                                )}
                                <VoiceToText
                                    transcript={textAreaValue}
                                    onTranscriptChange={(val) => {
                                        const normalizedVal = normalizeLineEndings(val);
                                        const truncatedVal =
                                            normalizedVal.length > props?.limit
                                                ? normalizedVal.substring(0, props?.limit)
                                                : normalizedVal;
                                        setTextAreaValue(truncatedVal);
                                        props?.onChange(truncatedVal.trim());
                                    }}
                                    tabIndex={props?.iconsTabIndex}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-between mt-1">
                {props?.createdByLabel && props?.createdByValue && (
                    <h6>
                        {props?.createdByLabel} : <span>{props?.createdByValue}</span>
                    </h6>
                )}
                {!props?.readOnly && (
                    <div className="lightText textSmall ms-auto">{`${textAreaValue.length}/${props?.limit} character limit`}</div>
                )}
            </div>
            {!props?.readOnly ? error && <div className="errorText">{error}</div> : ''}
        </>
    );
};

export default DescriptionBox;
